import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  Snackbar,
  TablePagination
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  MonetizationOn as MoneyIcon,
  Phone as PhoneIcon,
  AccountBalance as DrawerIcon
} from '@mui/icons-material';
import {
  getAllOrders,
  updateOrderStatus,
  updateOrderPaymentStatus,
  updateOrderItems,
  getStoreProducts,
  recordStoreOrderPayment
} from '../../services/storeService';
import { createOrderBostaDelivery } from '../../services/bostaService';
import {
  Order,
  OrderStatus,
  PaymentStatus,
  OrderItem
} from '../../types/store';
import { useAuth } from '../../contexts/AuthContext';
import { useStoreSettings } from '../../contexts/StoreSettingsContext';

type StoreOrderPaymentMethod = 'cash' | 'card' | 'bankTransfer' | 'vodafone_wallet' | 'instapay' | 'etisalat_wallet';

// ترجمة حالة الطلب
const translateOrderStatus = (status: OrderStatus) => {
  switch (status) {
    case OrderStatus.PENDING:
      return 'قيد الانتظار';
    case OrderStatus.PROCESSING:
      return 'قيد المعالجة';
    case OrderStatus.SHIPPED:
      return 'تم الشحن';
    case OrderStatus.DELIVERED:
      return 'تم التوصيل';
    case OrderStatus.CANCELLED:
      return 'ملغي';
    default:
      return 'غير معروف';
  }
};

// ترجمة حالة الدفع
const translatePaymentStatus = (status: PaymentStatus) => {
  switch (status) {
    case PaymentStatus.PAID:
      return 'مدفوع';
    case PaymentStatus.PENDING:
      return 'قيد الانتظار';
    case PaymentStatus.FAILED:
      return 'فشل الدفع';
    case PaymentStatus.REFUNDED:
      return 'تم رد المبلغ';
    default:
      return 'غير معروف';
  }
};

// تحديد لون حالة الطلب
const getOrderStatusColor = (status: OrderStatus) => {
  switch (status) {
    case OrderStatus.PENDING:
      return 'warning';
    case OrderStatus.PROCESSING:
      return 'info';
    case OrderStatus.SHIPPED:
      return 'primary';
    case OrderStatus.DELIVERED:
      return 'success';
    case OrderStatus.CANCELLED:
      return 'error';
    default:
      return 'default';
  }
};

// تحديد لون حالة الدفع
const getPaymentStatusColor = (status: PaymentStatus) => {
  switch (status) {
    case PaymentStatus.PAID:
      return 'success';
    case PaymentStatus.PENDING:
      return 'warning';
    case PaymentStatus.FAILED:
      return 'error';
    case PaymentStatus.REFUNDED:
      return 'info';
    default:
      return 'default';
  }
};

const OrdersManagementPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { settings } = useStoreSettings();
  const [orders, setOrders] = useState<Order[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [openOrderDialog, setOpenOrderDialog] = useState(false);
  const [openEditItemsDialog, setOpenEditItemsDialog] = useState(false);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<OrderStatus | ''>('');
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState<PaymentStatus | ''>('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editedItems, setEditedItems] = useState<OrderItem[]>([]);
  const [updatingOrder, setUpdatingOrder] = useState(false);

  // حالة نافذة تسجيل الدفع
  const [paymentMethod, setPaymentMethod] = useState<StoreOrderPaymentMethod>('cash');
  const [walletNumber, setWalletNumber] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);

  const electronicWalletMethods: StoreOrderPaymentMethod[] = ['vodafone_wallet', 'instapay', 'etisalat_wallet'];
  const availableCollectionMethods = React.useMemo<StoreOrderPaymentMethod[]>(() => {
    const paymentSettings = settings?.payment;
    const methods: StoreOrderPaymentMethod[] = [];

    if (!paymentSettings || paymentSettings.enableCashOnDelivery) {
      methods.push('cash');
    }

    if (!paymentSettings || paymentSettings.enableBankTransfer) {
      methods.push('bankTransfer');
    }

    if (paymentSettings?.enableElectronicPayment) {
      const activeGatewayIds = (paymentSettings.paymentGateways || [])
        .filter(gateway => gateway.isActive)
        .map(gateway => gateway.id.toLowerCase());

      if (activeGatewayIds.some(id => id.includes('vodafone'))) {
        methods.push('vodafone_wallet');
      }
      if (activeGatewayIds.some(id => id.includes('instapay'))) {
        methods.push('instapay');
      }
      if (activeGatewayIds.some(id => id.includes('etisalat'))) {
        methods.push('etisalat_wallet');
      }
      if (activeGatewayIds.some(id => !id.includes('vodafone') && !id.includes('instapay') && !id.includes('etisalat'))) {
        methods.push('card');
      }
    }

    return methods.length > 0 ? methods : ['cash'];
  }, [settings?.payment]);

  useEffect(() => {
    if (!availableCollectionMethods.includes(paymentMethod)) {
      setPaymentMethod(availableCollectionMethods[0]);
      setWalletNumber('');
    }
  }, [availableCollectionMethods, paymentMethod]);

  // تنسيق التاريخ
  const formatDate = (date: any) => {
    if (!date) return 'تاريخ غير متوفر';

    try {
      let dateObj: Date;

      // التحقق من نوع البيانات
      if (date instanceof Date) {
        dateObj = date;
      } else if (date.toDate && typeof date.toDate === 'function') {
        // Firestore Timestamp
        dateObj = date.toDate();
      } else if (date.seconds && date.nanoseconds) {
        // Firestore Timestamp في شكل كائن
        dateObj = new Date(date.seconds * 1000 + date.nanoseconds / 1000000);
      } else if (typeof date === 'string') {
        // سلسلة نصية
        dateObj = new Date(date);
      } else if (typeof date === 'number') {
        // طابع زمني بالميلي ثانية
        dateObj = new Date(date);
      } else {
        return 'تاريخ غير متوفر';
      }

      // التحقق من صحة التاريخ
      if (isNaN(dateObj.getTime())) {
        console.warn('Invalid date:', date);
        return 'تاريخ غير متوفر';
      }

      // تنسيق التاريخ بالتنسيق العربي
      return dateObj.toLocaleDateString('ar-EG');
    } catch (err) {
      console.error('Error formatting date:', err);
      return 'تاريخ غير متوفر';
    }
  };

  // تحميل الطلبات
  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await getAllOrders();
      setOrders(data);
      setFilteredOrders(data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  // تصفية الطلبات عند تغيير مصطلح البحث
  useEffect(() => {
    if (searchTerm) {
      const filtered = orders.filter(order =>
        order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customerPhone.includes(searchTerm) ||
        order.customerEmail.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOrders(filtered);
    } else {
      setFilteredOrders(orders);
    }
  }, [searchTerm, orders]);

  // تحميل المنتجات المتاحة
  const loadAvailableProducts = async () => {
    try {
      const result = await getStoreProducts();
      // نحن لا نستخدم المنتجات المتاحة في هذه الصفحة
      console.log(`Loaded ${result.products.length} products`);
    } catch (error) {
      console.error('Error loading available products:', error);
    }
  };

  // فتح حوار تفاصيل الطلب
  const handleOpenOrderDialog = (order: Order) => {
    setSelectedOrder(order);
    setSelectedStatus(order.orderStatus);
    setSelectedPaymentStatus(order.paymentStatus);
    setOpenOrderDialog(true);
  };

  // إغلاق حوار تفاصيل الطلب
  const handleCloseOrderDialog = () => {
    setSelectedOrder(null);
    setSelectedStatus('');
    setSelectedPaymentStatus('');
    setOpenOrderDialog(false);
  };

  // فتح حوار تعديل المنتجات
  const handleOpenEditItemsDialog = (order: Order) => {
    setSelectedOrder(order);
    setEditedItems([...order.items]);
    setOpenEditItemsDialog(true);
    loadAvailableProducts();
  };

  // إغلاق حوار تعديل المنتجات
  const handleCloseEditItemsDialog = () => {
    setSelectedOrder(null);
    setEditedItems([]);
    setOpenEditItemsDialog(false);
  };

  // تحديث حالة الطلب
  const handleUpdateOrderStatus = async () => {
    if (!selectedOrder || !selectedStatus) return;

    try {
      setUpdatingOrder(true);
      await updateOrderStatus(selectedOrder.id, selectedStatus);

      // تحديث الطلبات في الواجهة
      const updatedOrders = orders.map(order =>
        order.id === selectedOrder.id
          ? { ...order, orderStatus: selectedStatus }
          : order
      );

      setOrders(updatedOrders);
      setFilteredOrders(updatedOrders);

      setSnackbar({
        open: true,
        message: 'تم تحديث حالة الطلب بنجاح',
        severity: 'success'
      });

      handleCloseOrderDialog();
    } catch (error) {
      console.error('Error updating order status:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحديث حالة الطلب',
        severity: 'error'
      });
    } finally {
      setUpdatingOrder(false);
    }
  };

  // تحديث حالة الدفع
  const handleUpdatePaymentStatus = async () => {
    if (!selectedOrder || !selectedPaymentStatus) return;

    try {
      setUpdatingOrder(true);
      await updateOrderPaymentStatus(selectedOrder.id, selectedPaymentStatus);

      // تحديث الطلبات في الواجهة
      const updatedOrders = orders.map(order =>
        order.id === selectedOrder.id
          ? { ...order, paymentStatus: selectedPaymentStatus }
          : order
      );

      setOrders(updatedOrders);
      setFilteredOrders(updatedOrders);

      setSnackbar({
        open: true,
        message: 'تم تحديث حالة الدفع بنجاح',
        severity: 'success'
      });

      handleCloseOrderDialog();
    } catch (error) {
      console.error('Error updating payment status:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحديث حالة الدفع',
        severity: 'error'
      });
    } finally {
      setUpdatingOrder(false);
    }
  };

  // تحديث كمية المنتج
  const handleUpdateItemQuantity = (index: number, quantity: number) => {
    if (quantity < 0) return;

    const updatedItems = [...editedItems];
    const item = updatedItems[index];

    updatedItems[index] = {
      ...item,
      quantity,
      total: item.price * quantity
    };

    setEditedItems(updatedItems);
  };

  // تحديث سعر المنتج
  const handleUpdateItemPrice = (index: number, price: number) => {
    if (price < 0) return;

    const updatedItems = [...editedItems];
    const item = updatedItems[index];

    updatedItems[index] = {
      ...item,
      price,
      total: item.quantity * price
    };

    setEditedItems(updatedItems);
  };

  // حذف منتج من الطلب
  const handleRemoveItem = (index: number) => {
    const updatedItems = [...editedItems];
    updatedItems.splice(index, 1);
    setEditedItems(updatedItems);
  };

  // حساب إجمالي الطلب
  const calculateTotal = () => {
    return editedItems.reduce((sum, item) => sum + item.total, 0);
  };

  // حفظ تعديلات المنتجات
  const handleSaveItems = async () => {
    if (!selectedOrder || editedItems.length === 0) return;

    try {
      setUpdatingOrder(true);
      const total = calculateTotal();
      await updateOrderItems(selectedOrder.id, editedItems, total);

      // تحديث الطلبات في الواجهة
      const updatedOrders = orders.map(order =>
        order.id === selectedOrder.id
          ? { ...order, items: editedItems, total }
          : order
      );

      setOrders(updatedOrders);
      setFilteredOrders(updatedOrders);

      setSnackbar({
        open: true,
        message: 'تم تحديث منتجات الطلب بنجاح',
        severity: 'success'
      });

      handleCloseEditItemsDialog();
    } catch (error) {
      console.error('Error updating order items:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحديث منتجات الطلب',
        severity: 'error'
      });
    } finally {
      setUpdatingOrder(false);
    }
  };

  // تغيير الصفحة
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // تغيير عدد الصفوف في الصفحة
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // إغلاق رسالة التنبيه
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // فتح نافذة تسجيل الدفع
  const handleOpenPaymentDialog = (order: Order) => {
    setSelectedOrder(order);
    setPaymentMethod(availableCollectionMethods[0] || 'cash');
    setWalletNumber('');
    setPaymentNotes('');
    setOpenPaymentDialog(true);
  };

  // إغلاق نافذة تسجيل الدفع
  const handleClosePaymentDialog = () => {
    setOpenPaymentDialog(false);
    setSelectedOrder(null);
    setPaymentMethod(availableCollectionMethods[0] || 'cash');
    setWalletNumber('');
    setPaymentNotes('');
  };

  // تسجيل الدفع
  const handleRecordPayment = async () => {
    if (!selectedOrder || !currentUser) return;

    if (!availableCollectionMethods.includes(paymentMethod)) {
      setSnackbar({
        open: true,
        message: 'طريقة الدفع المختارة غير مفعلة في إعدادات المتجر',
        severity: 'error'
      });
      return;
    }

    // التحقق من رقم المحفظة للمحافظ الإلكترونية
    if (electronicWalletMethods.includes(paymentMethod) && !walletNumber.trim()) {
      setSnackbar({
        open: true,
        message: 'يرجى إدخال رقم المحفظة/الحساب المحول عليه',
        severity: 'error'
      });
      return;
    }

    try {
      setProcessingPayment(true);

      await recordStoreOrderPayment(
        selectedOrder.id,
        selectedOrder.total,
        paymentMethod,
        currentUser.id,
        currentUser.name || currentUser.email || 'مستخدم غير معروف',
        walletNumber || undefined,
        paymentNotes || undefined
      );

      // تحديث الطلبات في الواجهة
      const updatedOrders = orders.map(order =>
        order.id === selectedOrder.id
          ? { ...order, paymentStatus: PaymentStatus.PAID, orderStatus: OrderStatus.DELIVERED }
          : order
      );

      setOrders(updatedOrders);
      setFilteredOrders(updatedOrders);

      setSnackbar({
        open: true,
        message: 'تم تسجيل الدفع بنجاح',
        severity: 'success'
      });

      handleClosePaymentDialog();
    } catch (error) {
      console.error('Error recording payment:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تسجيل الدفع',
        severity: 'error'
      });
    } finally {
      setProcessingPayment(false);
    }
  };

  return (
    <Box>
      {/* عنوان الصفحة وأزرار التحكم */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">إدارة الطلبات</Typography>
      </Box>

      {/* شريط البحث */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs>
            <TextField
              fullWidth
              placeholder="البحث عن طلب..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
              }}
              variant="outlined"
              size="small"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* جدول الطلبات */}
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : filteredOrders.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            لا توجد طلبات متطابقة مع معايير البحث.
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>رقم الطلب</TableCell>
                  <TableCell>التاريخ</TableCell>
                  <TableCell>اسم العميل</TableCell>
                  <TableCell>رقم الهاتف</TableCell>
                  <TableCell>العنوان</TableCell>
                  <TableCell>المبلغ</TableCell>
                  <TableCell>حالة الطلب</TableCell>
                  <TableCell>حالة الدفع</TableCell>
                  <TableCell>الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOrders
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((order) => (
                    <TableRow key={order.id} hover>
                      <TableCell>{order.orderNumber}</TableCell>
                      <TableCell>
                        {formatDate(order.createdAt)}
                      </TableCell>
                      <TableCell>{order.customerName}</TableCell>
                      <TableCell>{order.customerPhone}</TableCell>
                      <TableCell>{order.shippingDetails?.address}, {order.shippingDetails?.city}</TableCell>
                      <TableCell>{order.total.toLocaleString('ar-EG')} ج.م</TableCell>
                      <TableCell>
                        <Chip
                          label={translateOrderStatus(order.orderStatus)}
                          color={getOrderStatusColor(order.orderStatus) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={translatePaymentStatus(order.paymentStatus)}
                          color={getPaymentStatusColor(order.paymentStatus) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenOrderDialog(order)}
                          title="تفاصيل الطلب"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEditItemsDialog(order)}
                          title="تعديل المنتجات"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        {order.paymentStatus === PaymentStatus.PENDING && (
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={() => handleOpenPaymentDialog(order)}
                            sx={{ ml: 1 }}
                          >
                            تسجيل الدفع
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredOrders.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="عدد الصفوف في الصفحة:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
          />
        </Paper>
      )}

      {/* حوار تفاصيل الطلب */}
      <Dialog
        open={openOrderDialog}
        onClose={handleCloseOrderDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          تفاصيل الطلب {selectedOrder?.orderNumber}
          <IconButton
            aria-label="close"
            onClick={handleCloseOrderDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedOrder && (
            <Grid container spacing={3}>
              {/* معلومات العميل */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  معلومات العميل
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1">
                    <strong>الاسم:</strong> {selectedOrder.customerName}
                  </Typography>
                  <Typography variant="body1">
                    <strong>الهاتف:</strong> {selectedOrder.customerPhone}
                  </Typography>
                  <Typography variant="body1">
                    <strong>البريد الإلكتروني:</strong> {selectedOrder.customerEmail}
                  </Typography>
                </Box>
              </Grid>

              {/* معلومات الشحن */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  معلومات الشحن
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1">
                    <strong>العنوان:</strong> {selectedOrder.shippingDetails?.address}
                  </Typography>
                  <Typography variant="body1">
                    <strong>المدينة:</strong> {selectedOrder.shippingDetails?.city}
                  </Typography>
                  <Typography variant="body1">
                    <strong>المنطقة:</strong> {selectedOrder.shippingDetails?.region}
                  </Typography>
                  <Typography variant="body1">
                    <strong>طريقة الشحن:</strong> {selectedOrder.shippingDetails?.shippingMethod}
                  </Typography>
                  {selectedOrder.bostaTrackingNumber && (
                    <>
                      <Typography variant="body1">
                        <strong>رقم تتبع Bosta:</strong> {selectedOrder.bostaTrackingNumber}
                      </Typography>
                      {selectedOrder.bostaStateName && (
                        <Typography variant="body1">
                          <strong>حالة الشحنة:</strong> <Chip label={selectedOrder.bostaStateName} size="small"
                            color={selectedOrder.bostaState === 45 ? 'success' : selectedOrder.bostaState === 49 ? 'error' : 'primary'} />
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Grid>

              {/* حالة الطلب */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  حالة الطلب
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>حالة الطلب</InputLabel>
                  <Select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value as OrderStatus)}
                    label="حالة الطلب"
                  >
                    <MenuItem value={OrderStatus.PENDING}>قيد الانتظار</MenuItem>
                    <MenuItem value={OrderStatus.PROCESSING}>قيد المعالجة</MenuItem>
                    <MenuItem value={OrderStatus.SHIPPED}>تم الشحن</MenuItem>
                    <MenuItem value={OrderStatus.DELIVERED}>تم التوصيل</MenuItem>
                    <MenuItem value={OrderStatus.CANCELLED}>ملغي</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleUpdateOrderStatus}
                  disabled={!selectedStatus || selectedStatus === selectedOrder.orderStatus || updatingOrder}
                >
                  {updatingOrder ? <CircularProgress size={24} /> : 'تحديث حالة الطلب'}
                </Button>
              </Grid>

              {/* حالة الدفع */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  حالة الدفع
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>حالة الدفع</InputLabel>
                  <Select
                    value={selectedPaymentStatus}
                    onChange={(e) => setSelectedPaymentStatus(e.target.value as PaymentStatus)}
                    label="حالة الدفع"
                  >
                    <MenuItem value={PaymentStatus.PENDING}>قيد الانتظار</MenuItem>
                    <MenuItem value={PaymentStatus.PAID}>مدفوع</MenuItem>
                    <MenuItem value={PaymentStatus.FAILED}>فشل الدفع</MenuItem>
                    <MenuItem value={PaymentStatus.REFUNDED}>تم رد المبلغ</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleUpdatePaymentStatus}
                  disabled={!selectedPaymentStatus || selectedPaymentStatus === selectedOrder.paymentStatus || updatingOrder}
                >
                  {updatingOrder ? <CircularProgress size={24} /> : 'تحديث حالة الدفع'}
                </Button>
              </Grid>

              {/* المنتجات */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  المنتجات
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>المنتج</TableCell>
                        <TableCell>الكمية</TableCell>
                        <TableCell>السعر</TableCell>
                        <TableCell>الإجمالي</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedOrder.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.productName}</TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell>{item.price.toLocaleString('ar-EG')} ج.م</TableCell>
                          <TableCell>{item.total.toLocaleString('ar-EG')} ج.م</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>

              {/* ملخص الطلب */}
              <Grid item xs={12}>
                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1">
                        <strong>المجموع الفرعي:</strong> {selectedOrder.subtotal.toLocaleString('ar-EG')} ج.م
                      </Typography>
                      <Typography variant="body1">
                        <strong>تكلفة الشحن:</strong> {selectedOrder.shippingCost.toLocaleString('ar-EG')} ج.م
                      </Typography>
                      {selectedOrder.discount > 0 && (
                        <Typography variant="body1">
                          <strong>الخصم:</strong> {selectedOrder.discount.toLocaleString('ar-EG')} ج.م
                        </Typography>
                      )}
                      {selectedOrder.tax > 0 && (
                        <Typography variant="body1">
                          <strong>الضريبة:</strong> {selectedOrder.tax.toLocaleString('ar-EG')} ج.م
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="h6" align="right">
                        <strong>الإجمالي:</strong> {selectedOrder.total.toLocaleString('ar-EG')} ج.م
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {selectedOrder?.shippingDetails?.shippingMethod === 'bosta' && !selectedOrder?.bostaDeliveryId && (
            <Button
              color="success"
              onClick={async () => {
                if (!selectedOrder) return;
                setUpdatingOrder(true);
                const result = await createOrderBostaDelivery(selectedOrder.id);
                if (result) {
                  setSnackbar({ open: true, message: 'تم إنشاء شحنة Bosta بنجاح - رقم التتبع: ' + result.trackingNumber, severity: 'success' });
                  loadOrders();
                  handleCloseOrderDialog();
                } else {
                  setSnackbar({ open: true, message: 'فشل إنشاء شحنة Bosta', severity: 'error' });
                }
                setUpdatingOrder(false);
              }}
              disabled={updatingOrder}
            >
              {updatingOrder ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
              إنشاء شحنة Bosta
            </Button>
          )}
          <Button onClick={handleCloseOrderDialog} color="primary">
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>

      {/* حوار تعديل المنتجات */}
      <Dialog
        open={openEditItemsDialog}
        onClose={handleCloseEditItemsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          تعديل منتجات الطلب {selectedOrder?.orderNumber}
          <IconButton
            aria-label="close"
            onClick={handleCloseEditItemsDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedOrder && (
            <>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>المنتج</TableCell>
                      <TableCell>الكمية</TableCell>
                      <TableCell>السعر</TableCell>
                      <TableCell>الإجمالي</TableCell>
                      <TableCell>الإجراءات</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {editedItems.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.productName}</TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={item.quantity}
                            onChange={(e) => handleUpdateItemQuantity(index, parseInt(e.target.value))}
                            inputProps={{ min: 0 }}
                            size="small"
                            sx={{ width: 80 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={item.price}
                            onChange={(e) => handleUpdateItemPrice(index, parseFloat(e.target.value))}
                            inputProps={{ min: 0, step: 0.01 }}
                            size="small"
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell>{item.total.toLocaleString('ar-EG')} ج.م</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveItem(index)}
                            color="error"
                            title="حذف المنتج"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                <Typography variant="h6" align="right">
                  <strong>الإجمالي:</strong> {calculateTotal().toLocaleString('ar-EG')} ج.م
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditItemsDialog} color="inherit">
            إلغاء
          </Button>
          <Button
            onClick={handleSaveItems}
            color="primary"
            variant="contained"
            disabled={updatingOrder}
          >
            {updatingOrder ? <CircularProgress size={24} /> : 'حفظ التغييرات'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* نافذة تسجيل الدفع */}
      <Dialog
        open={openPaymentDialog}
        onClose={handleClosePaymentDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          تسجيل دفع الطلب {selectedOrder?.orderNumber}
          <IconButton
            aria-label="close"
            onClick={handleClosePaymentDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedOrder && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>المبلغ المطلوب:</strong> {selectedOrder.total.toLocaleString('ar-EG')} ج.م
                </Alert>
              </Grid>

              {/* طريقة الدفع */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>طريقة الدفع</InputLabel>
                  <Select
                    value={paymentMethod}
                    label="طريقة الدفع"
                    onChange={(e) => {
                      const nextPaymentMethod = e.target.value as StoreOrderPaymentMethod;
                      setPaymentMethod(nextPaymentMethod);
                      if (!electronicWalletMethods.includes(nextPaymentMethod)) {
                        setWalletNumber('');
                      }
                    }}
                  >
                    <MenuItem value="cash" disabled={!availableCollectionMethods.includes('cash')}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <MoneyIcon fontSize="small" />
                        <Box>
                          <Typography variant="body2">نقدي</Typography>
                          <Typography variant="caption" color="text.secondary">Cash</Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                    <MenuItem value="vodafone_wallet" disabled={!availableCollectionMethods.includes('vodafone_wallet')}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PhoneIcon fontSize="small" />
                        <Box>
                          <Typography variant="body2">محفظة فودافون</Typography>
                          <Typography variant="caption" color="text.secondary">Vodafone Cash</Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                    <MenuItem value="instapay" disabled={!availableCollectionMethods.includes('instapay')}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <DrawerIcon fontSize="small" />
                        <Box>
                          <Typography variant="body2">انستاباي</Typography>
                          <Typography variant="caption" color="text.secondary">InstaPay</Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                    <MenuItem value="etisalat_wallet" disabled={!availableCollectionMethods.includes('etisalat_wallet')}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PhoneIcon fontSize="small" />
                        <Box>
                          <Typography variant="body2">محفظة اتصالات</Typography>
                          <Typography variant="caption" color="text.secondary">Etisalat Cash</Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                    <MenuItem value="card" disabled={!availableCollectionMethods.includes('card')}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <MoneyIcon fontSize="small" />
                        <Box>
                          <Typography variant="body2">بطاقة ائتمان</Typography>
                          <Typography variant="caption" color="text.secondary">Credit Card</Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                    <MenuItem value="bankTransfer" disabled={!availableCollectionMethods.includes('bankTransfer')}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <DrawerIcon fontSize="small" />
                        <Box>
                          <Typography variant="body2">تحويل بنكي</Typography>
                          <Typography variant="caption" color="text.secondary">Bank Transfer</Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* رقم المحفظة/الحساب */}
              {electronicWalletMethods.includes(paymentMethod) && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="رقم المحفظة/الحساب المحول عليه"
                    value={walletNumber}
                    onChange={(e) => setWalletNumber(e.target.value)}
                    placeholder={
                      paymentMethod === 'vodafone_wallet' ? '01xxxxxxxxx' :
                      paymentMethod === 'instapay' ? '1234567890123456' :
                      '01xxxxxxxxx'
                    }
                    required
                    helperText={`مطلوب لتأكيد ${
                      paymentMethod === 'vodafone_wallet' ? 'فودافون كاش' :
                      paymentMethod === 'instapay' ? 'انستاباي' :
                      'اتصالات كاش'
                    }`}
                  />
                </Grid>
              )}

              {/* ملاحظات */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="ملاحظات (اختياري)"
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  multiline
                  rows={2}
                  placeholder="أي ملاحظات إضافية..."
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePaymentDialog} color="inherit" disabled={processingPayment}>
            إلغاء
          </Button>
          <Button
            onClick={handleRecordPayment}
            color="success"
            variant="contained"
            disabled={processingPayment}
          >
            {processingPayment ? <CircularProgress size={24} /> : 'تأكيد الدفع'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* رسالة التنبيه */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrdersManagementPage;
